import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { FormEvent, useCallback, useEffect, useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../../components/common/toolbarStyles";
import { AttendanceMessagesResponse, getAttendanceMessages, retryAttendanceMessages } from "../../shared/AttendanceAPI";
import { GradeSectionComponent } from "../../shared/GradeSectionComponent";
import { getApiErrorMessage } from "../../shared/util/ApiErrorUtil";
import { AttendanceMessageStatus, MessageStatusRow } from "./AttendanceMessageStatus";

/** While any message is PENDING the table refreshes this often. */
const POLL_MS = 10_000;

type Searched = { grade: string; section: string; date: string };

/** Students attendance → Message Status tab: SMS status per Absent / Late student, with retry. */
export const StudentMessageStatus = ({ tabs }: { tabs: React.ReactNode }) => {
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [searched, setSearched] = useState<Searched>();
  const [data, setData] = useState<AttendanceMessagesResponse>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const load = useCallback(async (filter: Searched, showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getAttendanceMessages(filter.grade, filter.section, filter.date);
      setData(res.data);
      setError("");
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  const search = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // same rules as the backend: grade, section and a date not in the future
    if (!grade || !section) return setError("Select grade and section.");
    if (!date || !date.isValid()) return setError("Select a valid date.");
    if (date.isAfter(dayjs(), "day")) return setError("Date cannot be in the future.");
    const filter = { grade, section, date: date.format("YYYY-MM-DD") };
    setInfo("");
    setSearched(filter);
    load(filter, true);
  };

  const hasPending = !!data?.messages?.some((m) => m.messageStatus === "PENDING");
  useEffect(() => {
    if (!searched || !hasPending) return;
    const timer = setInterval(() => load(searched, false), POLL_MS);
    return () => clearInterval(timer);
  }, [searched, hasPending, load]);

  const retry = async (ids: number[]) => {
    if (!searched) return;
    try {
      const res = await retryAttendanceMessages(searched.grade, searched.section, ids);
      const { queued, ignored } = res.data;
      setInfo(`${queued} message(s) queued for retry` + (ignored > 0 ? `, ${ignored} skipped (no longer failed).` : "."));
      setError("");
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
    await load(searched, false);
  };

  const isToday = searched?.date === dayjs().format("YYYY-MM-DD");
  const retryDisabledReason = !data?.messageSendingEnabled
    ? "Message sending is turned off in Settings."
    : !isToday
      ? "Only today's messages can be retried."
      : undefined;

  const rows: MessageStatusRow[] = (data?.messages ?? []).map((m) => ({
    id: m.attendanceId,
    code: m.admissionNo,
    name: m.studentName,
    attendance: m.attendance,
    messageStatus: m.messageStatus,
    failureReason: m.failureReason,
    attemptCount: m.attemptCount,
    lastAttemptAt: m.lastAttemptAt,
  }));

  return (
    <>
      <PageTopBar
        left={tabs}
        right={
          <Box component="form" display="flex" alignItems="center" gap={1} flexWrap="wrap" onSubmit={search} sx={toolbarFieldsSx}>
            <GradeSectionComponent grade={grade} setGrade={setGrade} section={section} setSection={setSection} inline />
            <DatePicker
              value={date}
              onChange={setDate}
              format="DD-MMM-YYYY"
              disableFuture
              slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
            />
            <Button type="submit" variant="outlined" size="small" sx={toolbarButtonSx}>
              Get
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setInfo("")}>
          {info}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <AttendanceMessageStatus
          rows={rows}
          codeLabel="Admission No"
          onRetry={retry}
          retryDisabledReason={retryDisabledReason}
          emptyMessage={
            searched
              ? `No absent or late students in ${searched.grade} – ${searched.section} on ${dayjs(searched.date).format("DD-MMM-YYYY")}.`
              : "Select grade, section and date, then click Get to see message status."
          }
        />
      )}
    </>
  );
};
